/**
 * NP-45 — theme state hook.
 *
 * Responsibilities:
 *  - initialise from the resolved theme (stored → system) so the toggle matches
 *    what the pre-paint script already applied;
 *  - on sign-in, restore the theme persisted on the account record (AC3) so it
 *    follows the user across devices;
 *  - on change, apply immediately + persist locally AND to the account record.
 */
import { useCallback, useEffect, useState } from "react";
import { applyTheme, resolveInitialTheme, type Theme } from "@/lib/theme";
import { settingsService } from "@/services/settings";
import { useAuth } from "@/contexts/AuthContext";

export function useTheme() {
  const { isAuthenticated } = useAuth();
  const [theme, setThemeState] = useState<Theme>(() => resolveInitialTheme());

  // Keep the document + local persistence in sync with state.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // AC3: restore the account-persisted theme once the user is authenticated
  // (cross-device). Local/system default already applied for the first paint.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    void settingsService.getTheme().then((accountTheme) => {
      if (!cancelled && accountTheme) setThemeState(accountTheme);
    });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const setTheme = useCallback(
    (next: Theme) => {
      setThemeState(next);
      applyTheme(next);
      // Persist to the account record — non-blocking; local persistence already
      // guarantees the choice survives a reload even if the API call fails.
      void settingsService.updateTheme(next).catch(() => {});
    },
    [],
  );

  const toggle = useCallback(() => {
    setThemeState((prev) => {
      const next: Theme = prev === "dark" ? "light" : "dark";
      applyTheme(next);
      void settingsService.updateTheme(next).catch(() => {});
      return next;
    });
  }, []);

  return { theme, isDark: theme === "dark", setTheme, toggle };
}
